import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import PublicRoute from '../components/auth/PublicRoute';
import DashboardLayout from '../components/layout/DashboardLayout';
import LoginPage from '../pages/auth/LoginPage';
import AdminProfilePage from '../pages/account/AdminProfilePage';
import AdminUserGuidePage from '../pages/admin/AdminUserGuidePage';
import AdminsRbacPage from '../pages/admin/AdminsRbacPage';
import BillingPage from '../pages/billing/BillingPage';
import ConfigurationsPage from '../pages/admin/ConfigurationsPage';
import DashboardOverview from '../pages/dashboard/DashboardOverview';
import KycVerificationPage from '../pages/kyc/KycVerificationPage';
import LegalDocumentsPage from '../pages/admin/LegalDocumentsPage';
import AdminLogsPage from '../pages/logs/AdminLogsPage';
import MonitoringPage from '../pages/monitoring/MonitoringPage';
import JobInsightsPage from '../pages/monitoring/JobInsightsPage';
import QuestionBanksPage from '../pages/admin/QuestionBanksPage';
import NotificationsPage from '../pages/admin/NotificationsPage';
import SupportTicketsPage from '../pages/support/SupportTicketsPage';
import TaxonomyPage from '../pages/admin/TaxonomyPage';
import WhatsappSenderPage from '../pages/admin/WhatsappSenderPage';
import CandidatesListPage from '../pages/candidates/CandidatesListPage';
import EmployersListPage from '../pages/employers/EmployersListPage';
import AssessmentsListPage from '../pages/hiring/AssessmentsListPage';
import InterviewsListPage from '../pages/hiring/InterviewsListPage';
import OffersListPage from '../pages/hiring/OffersListPage';
import JobsListPage from '../pages/jobs/JobsListPage';
import { APP_ROUTES } from '../constants';

const DefaultRedirect = () => <Navigate to={APP_ROUTES.HOME} replace />;

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={(
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        )}
      />

      <Route
        path="/"
        element={(
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        )}
      >
        <Route index element={<DefaultRedirect />} />
        <Route
          path="profile"
          element={(
            <ProtectedRoute>
              <AdminProfilePage />
            </ProtectedRoute>
          )}
        />
        <Route
          path="user-guide"
          element={(
            <ProtectedRoute>
              <AdminUserGuidePage />
            </ProtectedRoute>
          )}
        />
        <Route
          path="dashboard"
          element={(
            <ProtectedRoute permission="dashboard.view">
              <DashboardOverview />
            </ProtectedRoute>
          )}
        />
        <Route
          path="employers"
          element={(
            <ProtectedRoute permission="monitoring.view">
              <EmployersListPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path="employers/kyc"
          element={(
            <ProtectedRoute permission="kyc.review">
              <KycVerificationPage />
            </ProtectedRoute>
          )}
        />
        <Route path="kyc" element={<Navigate to="/employers/kyc" replace />} />

        <Route
          path="candidates"
          element={(
            <ProtectedRoute permission="monitoring.view">
              <CandidatesListPage />
            </ProtectedRoute>
          )}
        />

        <Route
          path="jobs"
          element={(
            <ProtectedRoute permission="monitoring.view">
              <JobsListPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path="jobs/insights"
          element={(
            <ProtectedRoute permission="monitoring.view">
              <JobInsightsPage />
            </ProtectedRoute>
          )}
        />
        <Route path="job-insights" element={<Navigate to="/jobs/insights" replace />} />

        <Route
          path="interviews"
          element={(
            <ProtectedRoute permission="monitoring.view">
              <InterviewsListPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path="assessments"
          element={(
            <ProtectedRoute permission="monitoring.view">
              <AssessmentsListPage />
            </ProtectedRoute>
          )}
        />

        <Route
          path="offers"
          element={(
            <ProtectedRoute permission="monitoring.view">
              <OffersListPage />
            </ProtectedRoute>
          )}
        />

        <Route
          path="configurations"
          element={(
            <ProtectedRoute permission="config.manage">
              <ConfigurationsPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path="configurations/taxonomy"
          element={(
            <ProtectedRoute permission="config.manage">
              <TaxonomyPage />
            </ProtectedRoute>
          )}
        />
        <Route path="taxonomy" element={<Navigate to="/configurations/taxonomy" replace />} />
        <Route
          path="configurations/question-banks"
          element={(
            <ProtectedRoute permission="question_banks.view">
              <QuestionBanksPage />
            </ProtectedRoute>
          )}
        />
        <Route path="question-banks" element={<Navigate to="/configurations/question-banks" replace />} />
        <Route
          path="configurations/legal-documents"
          element={(
            <ProtectedRoute permission="config.manage">
              <LegalDocumentsPage />
            </ProtectedRoute>
          )}
        />
        <Route path="legal-documents" element={<Navigate to="/configurations/legal-documents" replace />} />
        <Route
          path="configurations/admins"
          element={(
            <ProtectedRoute permission="admins.manage">
              <AdminsRbacPage />
            </ProtectedRoute>
          )}
        />
        <Route path="admins" element={<Navigate to="/configurations/admins" replace />} />

        <Route
          path="communications/notifications"
          element={(
            <ProtectedRoute permission="notifications.view">
              <NotificationsPage />
            </ProtectedRoute>
          )}
        />
        <Route path="notifications" element={<Navigate to="/communications/notifications" replace />} />
        <Route
          path="communications/whatsapp"
          element={(
            <ProtectedRoute permission="notifications.view">
              <WhatsappSenderPage />
            </ProtectedRoute>
          )}
        />

        <Route
          path="support/tickets"
          element={(
            <ProtectedRoute permission="tickets.view">
              <SupportTicketsPage />
            </ProtectedRoute>
          )}
        />
        <Route path="tickets" element={<Navigate to="/support/tickets" replace />} />

        <Route
          path="finance/billing"
          element={(
            <ProtectedRoute permission="billing.view">
              <BillingPage />
            </ProtectedRoute>
          )}
        />
        <Route path="billing" element={<Navigate to="/finance/billing" replace />} />

        <Route
          path="logs"
          element={(
            <ProtectedRoute permission="logs.view">
              <AdminLogsPage domain="overview" />
            </ProtectedRoute>
          )}
        />
        <Route
          path="logs/employers"
          element={(
            <ProtectedRoute permission="logs.view">
              <AdminLogsPage domain="employers" />
            </ProtectedRoute>
          )}
        />
        <Route
          path="logs/candidates"
          element={(
            <ProtectedRoute permission="logs.view">
              <AdminLogsPage domain="candidates" />
            </ProtectedRoute>
          )}
        />
        <Route
          path="logs/jobs"
          element={(
            <ProtectedRoute permission="logs.view">
              <AdminLogsPage domain="jobs" />
            </ProtectedRoute>
          )}
        />
        <Route
          path="logs/interviews"
          element={(
            <ProtectedRoute permission="logs.view">
              <AdminLogsPage domain="interviews" />
            </ProtectedRoute>
          )}
        />
        <Route
          path="logs/assessments"
          element={(
            <ProtectedRoute permission="logs.view">
              <AdminLogsPage domain="assessments" />
            </ProtectedRoute>
          )}
        />
        <Route
          path="logs/offers"
          element={(
            <ProtectedRoute permission="logs.view">
              <AdminLogsPage domain="offers" />
            </ProtectedRoute>
          )}
        />

        <Route
          path="monitoring"
          element={(
            <ProtectedRoute permission="monitoring.view">
              <MonitoringPage />
            </ProtectedRoute>
          )}
        />
      </Route>

      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

export default AppRoutes;
